import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { BrainCircuit, Loader2, ArrowRight, TrendingUp, Target, Sparkles } from "lucide-react";

export default function IQShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get(`/iq/share/${shareId}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("error"));
  }, [shareId]);

  return (
    <div className="min-h-screen bg-[#050505] glow-grid text-slate-200 flex flex-col">
      <header className="glass border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-bold text-white" data-testid="iq-share-logo">ECO</Link>
          <Link to="/login" className="btn-secondary text-sm" data-testid="iq-share-signin">Sign in</Link>
        </div>
      </header>

      <div className="flex-1 max-w-2xl w-full mx-auto px-5 py-16">
        {state === "loading" && <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#4d8bff]" /></div>}

        {state === "error" && (
          <div className="text-center py-24" data-testid="iq-share-notfound">
            <h1 className="font-display text-2xl font-bold text-white">Result not found</h1>
            <p className="text-slate-400 mt-3">This shared result link may be invalid or no longer available.</p>
            <Link to="/login" className="btn-accent mt-8 inline-flex" data-testid="iq-share-cta-fallback">Take the Business IQ Test <ArrowRight size={16} /></Link>
          </div>
        )}

        {state === "ok" && data && (
          <div className="space-y-8" data-testid="iq-share">
            <div className="text-center">
              <span className="eco-tag inline-flex items-center gap-2 font-mono-eco uppercase tracking-widest mb-2"><Sparkles size={13} className="text-[#A855F7]" /> Business IQ Result</span>
            </div>

            <div className="eco-card glow-grid p-10 text-center" data-testid="iq-share-card">
              <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">{data.category} · Business IQ</span>
              <div className="font-display text-7xl font-bold text-white mt-3" data-testid="iq-share-score">{data.overall}<span className="text-3xl text-slate-500">%</span></div>
              <p className="text-slate-400 mt-2">{data.overall >= 80 ? "Thinks like a founder." : data.overall >= 50 ? "A solid business foundation." : "A promising starting point."}</p>
              <div className="grid grid-cols-2 gap-3 mt-8 text-left">
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                  <div className="flex items-center gap-2 text-emerald-400 text-[11px] font-semibold uppercase tracking-wide"><TrendingUp size={14} /> Top strength</div>
                  <div className="text-white font-medium mt-1.5 text-sm">{data.top_strength || "—"}</div>
                </div>
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                  <div className="flex items-center gap-2 text-amber-400 text-[11px] font-semibold uppercase tracking-wide"><Target size={14} /> Growth area</div>
                  <div className="text-white font-medium mt-1.5 text-sm">{data.weakest_area || "—"}</div>
                </div>
              </div>
            </div>

            <div className="eco-card p-8 text-center border-[#0055FF]/40" style={{ boxShadow: "0 0 40px rgba(0,85,255,0.12)" }}>
              <div className="h-14 w-14 rounded-2xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center mx-auto mb-4"><BrainCircuit size={26} className="text-[#4d8bff]" /></div>
              <h2 className="font-display text-2xl font-bold text-white">What's your Business IQ?</h2>
              <p className="text-slate-400 mt-3 max-w-md mx-auto">Test your business thinking across 9 categories and get a personalized AI roadmap — free on Entrepreneur OS.</p>
              <Link to="/login" data-testid="iq-share-cta" className="btn-accent mt-7 inline-flex">Take the test free <ArrowRight size={16} /></Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
